<template>
  <div class="lei-page">
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3">
        <div class="lei-top">
          <div>
            <div class="page-titlerow">
              <h2 class="lei-title">Live events</h2>
              <button type="button" class="page-helpbtn" :class="{ 'is-open': showHelp }" :aria-expanded="showHelp" @click="showHelp = !showHelp">
                <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
              </button>
            </div>
            <div class="lei-sub">{{ summary }}</div>
          </div>
        </div>

        <div v-show="showHelp" class="page-help">
          <div class="page-help__sec">
            <div class="page-help__label">What is listed</div>
            <ul>
              <li>A match is listed when the current time is inside its start–end window, or when one of its active channels has status on (early start or overrun).</li>
              <li>The match must have at least one <b>active</b> channel to appear in <b>Live now</b>. Its active channels are shown in their channel order. Times are in your local time.</li>
              <li>A match that is inside its start–end window but has no active channels is listed lower down, under <b>Should be live, not configured</b>, with the reason: no channels are configured for it, or channels exist but none are active. The page summary counts these as "not configured".</li>
              <li>The stream data is read from the encoder every 30 seconds, one request per encoder. The match list reloads about every 90 seconds. Refresh does both now. Refreshing pauses while you drag or save.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Stream strip, in order</div>
            <ul>
              <li><b>Badge:</b> streaming, stopped, no data (the stream id is not in the encoder's list), unreachable, not linked (no encoder or stream id on the channel) or loading. If the encoder cannot be reached the last known values stay, marked stale with the time they were read.</li>
              <li><b>Stream name</b> and then the stats line: <b>bitrate · fps · uptime · speed · errors</b>. Speed 1.0x is real time.</li>
              <li><b>Encoder → origin</b> on the right is where the stream is read from.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Channel row</div>
            <ul>
              <li><b>status on / off</b> is the channel's own status. A warning appears when it disagrees with the encoder (status on but not streaming, or streaming with status off).</li>
              <li><b>active / inactive</b> can be clicked to hide a channel from users or show it again. It saves immediately.</li>
              <li><b>Configuration warnings</b> (an amber banner at the top, and under the channel) appear when the channel does not match its encoder stream: the output URL points to another channel, the output origin differs from the channel's origin, the stream name differs, or the origin or stream name is empty. The encoder stream is read once per live channel and re-checked every 5 minutes.</li>
              <li><b>More info</b> loads the stream's inputs and output URL from the encoder and warns if the output points at a different channel. The chart icon opens the Kibana dashboard.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Start and Stop</div>
            <ul>
              <li><b>Stop</b> (on a running channel) and <b>Stop all</b> stop the stream on the encoder and mark the channel as stopped. A popup asks you to confirm first.</li>
              <li><b>Start</b> and <b>Start all</b> appear only while the match is inside its time window and the stream is stopped. They start the stream on the encoder. A <b>Not streaming</b> badge on the match means it should be live but nothing is running.</li>
              <li>Neither is available while the encoder is unreachable.</li>
              <li><b>Reorder</b> lets you drag the channels of one match and saves the order when you click Done. Start and Stop buttons (including Start all and Stop all) are disabled on every match while you reorder.</li>
            </ul>
          </div>
        </div>

        <div class="lei-bar">
          <div class="lei-tools">
            <span class="lei-count lei-mono">
              <template v-if="refreshPaused">refresh paused</template>
              <template v-else>next poll &middot; {{ countdown }}s</template>
            </span>
            <button type="button" class="lei-btn" @click="manualRefresh" :disabled="pollInFlight || refreshPaused">Refresh</button>
          </div>
          <div v-if="distinctEncoders.length" class="lei-encoders">
            <span v-for="enc in distinctEncoders" :key="enc" class="lei-enc" :class="'lei-enc--' + encoderTone(enc)">
              <span class="lei-enc__dot"></span><b>{{ enc }}</b> {{ encoderLabel(enc) }}
            </span>
          </div>
        </div>

        <div v-if="configWarnings.length" class="lei-banner">
          <b>{{ configWarnings.length }} {{ configWarnings.length === 1 ? 'channel has' : 'channels have' }} a configuration problem.</b>
          <div v-for="w in configWarnings" :key="w.calChannelId" class="lei-banner__item">
            <b>{{ w.channelName }}</b> ({{ w.match }}): {{ w.issues.join(' ') }}
          </div>
        </div>

        <div v-for="enc in unreachableEncoders" :key="'down-' + enc" class="lei-banner">
          <b>{{ enc }}</b> unreachable. {{ staleNote(enc) }}
        </div>

        <div v-if="loadError" class="error-message mt-3">{{ loadError }}</div>
        <div v-if="loading" class="lei-muted mt-3">Loading...</div>

        <template v-else>
          <div class="lei-section">Live now</div>
          <div v-if="!liveMatches.length" class="lei-muted">
            {{ unconfiguredMatches.length ? 'No configured matches are live right now.' : 'No live matches right now.' }}
          </div>

          <div v-for="match in liveMatches" :key="match.calMatchId" class="lei-card">
            <div class="lei-card__head">
              <span class="lei-pill lei-pill--live">{{ inWindow(match) ? 'Live' : 'Pre-match' }}</span>
              <span v-if="shouldBeLiveIdle(match)" class="lei-pill lei-pill--warn" title="The match is in its time window but no stream is running">Not streaming</span>
              <span v-if="match.format" class="lei-pill lei-pill--tag">{{ match.format }}</span>
              <span class="lei-card__teams">{{ match.team1 }} v {{ match.team2 }}</span>
            </div>
            <div class="lei-card__meta">
              <span class="lei-card__meta-text">{{ leagueNameFor(match.calLeagueId) }} &middot; {{ formatRange(match) }}</span>
              <div class="lei-card__action">
                <button v-if="startableFor(match).length" type="button"
                        class="lei-btn lei-btn--sm lei-btn--start"
                        :disabled="matchBusy(match) || reorderActive" :title="reorderActive ? reorderLockedTitle : ''" @click="askStart(startableFor(match))">
                  Start all
                </button>
                <button v-if="stoppableFor(match).length" type="button"
                        class="lei-btn lei-btn--sm lei-btn--danger"
                        :disabled="matchBusy(match) || reorderActive" :title="reorderActive ? reorderLockedTitle : ''" @click="askStop(stoppableFor(match))">
                  Stop all
                </button>
                <button v-if="channelsFor(match).length > 1" type="button"
                        class="lei-btn lei-btn--sm" :class="{ 'lei-btn--on': isReordering(match) }"
                        :disabled="savingOrder || (reorderMatchId !== null && !isReordering(match))"
                        @click="isReordering(match) ? finishReorder(match) : startReorder(match)">
                  {{ isReordering(match) ? (savingOrder ? 'Saving…' : 'Done') : 'Reorder' }}
                </button>
              </div>
            </div>
            <div v-if="orderErrors[match.calMatchId]" class="lei-row__error">{{ orderErrors[match.calMatchId] }}</div>
            <div class="lei-rows">
              <div v-if="!channelsFor(match).length" class="lei-empty">No active channels configured.</div>
              <Draggable
                :model-value="channelsFor(match)"
                item-key="calChannelId"
                handle=".lei-drag:not(.lei-drag--off)"
                :disabled="!isReordering(match) || savingOrder"
                :animation="150"
                :scroll="true"
                :scroll-sensitivity="80"
                :fallback-tolerance="5"
                ghost-class="drag-ghost"
                @start="dragging = true"
                @end="dragging = false"
                @update:model-value="onReorder(match, $event)"
              >
                <template #item="{ element: ch }">
                  <LiveChannelRow
                    :channel="ch"
                    :channel-record="channelByName[ch.channelName] || null"
                    :channel-records-loaded="channelRecordsLoaded"
                    :encoder-state-entry="ch.encoder ? (encoderState[ch.encoder.trim()] || null) : null"
                    :reorder-mode="isReordering(match)"
                    :actions-locked="reorderActive"
                    :busy="!!actionState[ch.calChannelId]?.busy"
                    :should-be-live="inWindow(match)"
                    :config-issues="configIssuesFor(ch)"
                    :action-error="actionState[ch.calChannelId]?.error || ''"
                    @stop="askStop([ch])"
                    @start="askStart([ch])"
                    @toggle-active="toggleActive(ch, $event)"
                  />
                </template>
              </Draggable>
            </div>
          </div>
        </template>

        <template v-if="!loading && unconfiguredMatches.length">
          <div class="lei-section lei-section--gap">Should be live, not configured</div>
          <div v-for="match in unconfiguredMatches" :key="match.calMatchId" class="lei-card">
            <div class="lei-card__head">
              <span class="lei-pill lei-pill--warn">Not configured</span>
              <span v-if="match.format" class="lei-pill lei-pill--tag">{{ match.format }}</span>
              <span class="lei-card__teams">{{ match.team1 }} v {{ match.team2 }}</span>
            </div>
            <div class="lei-card__meta">
              <span class="lei-card__meta-text">{{ leagueNameFor(match.calLeagueId) }} &middot; {{ formatRange(match) }}</span>
            </div>
            <div class="lei-empty">{{ unconfiguredReason(match) }}</div>
          </div>
        </template>

        <div class="lei-foot">
          Times in your local time ({{ timezoneLabel }}).
        </div>
      </div>
    </div>

    <ConfirmModal :show="stopTargets.length > 0" :title="stopTitle" confirm-label="Stop" danger
                  @confirm="confirmStop" @close="stopTargets = []">
      <p class="mb-2">This stops the stream on the encoder and marks the channel as stopped.</p>
      <ul class="mb-0">
        <li v-for="ch in stopTargets" :key="ch.calChannelId"><b>{{ ch.channelName }}</b> ({{ ch.encoder }} #{{ ch.encStreamId }})</li>
      </ul>
    </ConfirmModal>

    <ConfirmModal :show="startTargets.length > 0" :title="startTitle" confirm-label="Start"
                  @confirm="confirmStart" @close="startTargets = []">
      <p class="mb-2">This starts the stream on the encoder.</p>
      <ul class="mb-0">
        <li v-for="ch in startTargets" :key="ch.calChannelId"><b>{{ ch.channelName }}</b> ({{ ch.encoder }} #{{ ch.encStreamId }})</li>
      </ul>
    </ConfirmModal>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import AdminService from '@/services/admin-service';
import XaccelService from '@/services/xaccel-service';
import Header from '@/components/Header.vue';
import LiveChannelRow from '@/components/LiveChannelRow.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { feedWindowMs } from '@/utils/general-functions';
import { statsMapFromList, isStreamRunning, isLinkedChannel, checkChannelConfig, sortChannelsByOrder, encStreamIdentifier } from '@/utils/bapi-functions';

const POLL_SECONDS = 30;
const MATCH_REFRESH_MS = 90 * 1000;
const CONFIG_CHECK_MS = 5 * 60 * 1000;

export default {
  name: 'LiveEvents',
  components: { Header, LiveChannelRow, Draggable, ConfirmModal },
  beforeRouteLeave() {
    if (this.reorderMatchId !== null && this.hasPendingChanges) {
      if (!window.confirm('You have unsaved changes that will be lost if you leave. Continue?')) return false;
    }
  },
  data() {
    return {
      calLeaguesList: [],
      calMatchesList: [],
      calChannelsList: [],
      channelRecords: [],
      channelRecordsLoaded: false,
      loading: false,
      loadError: '',
      // { [encoder]: { stats: Map|null, lastOkAt: Date|null, unreachable: bool, pending: bool } }
      encoderState: {},
      countdown: POLL_SECONDS,
      pollInFlight: false,
      // calMatchId of the match whose channels are being reordered (one at a time)
      reorderMatchId: null,
      // { [calChannelId]: { busy: bool, error: string } }
      actionState: {},
      dragging: false,
      savingOrder: false,
      // { [calMatchId]: calChannelId[] } - order shown right after a drop, until the saved channel records reload
      localOrder: {},
      orderErrors: {},
      showHelp: false,
      // { [calChannelId]: { meta, at } } - encoder stream metadata used to check the channel setup
      configMeta: {},
      stopTargets: [],
      startTargets: []
    };
  },
  computed: {
    // Any match being reordered: Start/Stop are disabled everywhere until Done.
    reorderActive() {
      return this.reorderMatchId !== null;
    },
    reorderLockedTitle() {
      return 'Finish reordering (click Done) to start or stop streams';
    },
    refreshPaused() {
      return this.dragging || this.savingOrder;
    },
    timezoneLabel() {
      const off = -new Date().getTimezoneOffset();
      const abs = Math.abs(off);
      const hh = String(Math.floor(abs / 60)).padStart(2, '0');
      const mm = String(abs % 60).padStart(2, '0');
      return `UTC${off >= 0 ? '+' : '-'}${hh}:${mm}`;
    },
    leaguesById() {
      const map = {};
      for (const lg of this.calLeaguesList) map[lg.calLeagueId] = lg;
      return map;
    },
    channelByName() {
      const map = {};
      for (const c of this.channelRecords) {
        if (!map[c.channelName]) map[c.channelName] = c;
      }
      return map;
    },
    channelsByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!ch.active) continue;
        (map[ch.calMatchId] = map[ch.calMatchId] || []).push(ch);
      }
      const orderOf = (ch) => {
        const n = this.channelByName[ch.channelName]?.orderNumber;
        return n !== null && n !== undefined ? n : Infinity;
      };
      for (const id of Object.keys(map)) {
        map[id] = sortChannelsByOrder(map[id]).sort((a, b) => {
          const oa = orderOf(a);
          const ob = orderOf(b);
          return oa === ob ? 0 : (oa < ob ? -1 : 1);
        });
        const local = this.localOrder[id];
        if (local) {
          const pos = (ch) => {
            const i = local.indexOf(ch.calChannelId);
            return i === -1 ? Infinity : i;
          };
          map[id] = map[id].slice().sort((a, b) => pos(a) - pos(b));
        }
      }
      return map;
    },
    // Active channels whose feed window (match start - lead .. match end + trail) is open now - the feed runs before the match
    // starts, and that is when viewers already arrive. Their encoders are polled even if the match is not live yet.
    feedWindowChannels() {
      const now = Date.now();
      const matchById = new Map(this.calMatchesList.map(m => [m.calMatchId, m]));
      return this.calChannelsList.filter(ch => {
        if (!ch.active || !ch.encoder) return false;
        const w = feedWindowMs(ch, matchById.get(ch.calMatchId));
        return !!w && w.start <= now && now < w.end;
      });
    },
    // Matches that should be live (inside their start-end window) or already have a running feed: the channel's live
    // flag is on or - inside its feed window - the encoder reports the stream running.
    shouldBeLiveMatches() {
      const now = new Date();
      const feedOpen = new Set(this.feedWindowChannels.map(ch => ch.calChannelId));
      return this.calMatchesList
        .filter(m => {
          if (!m.startTime) return false;
          if (new Date(m.startTime) <= now && now < new Date(this.effectiveEnd(m))) return true;
          return (this.channelsByMatch[m.calMatchId] || []).some(ch =>
            ch.status === true || (feedOpen.has(ch.calChannelId) && this.isRunning(ch)));
        })
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    },
    liveMatches() {
      return this.shouldBeLiveMatches.filter(m => (this.channelsByMatch[m.calMatchId] || []).length > 0);
    },
    unconfiguredMatches() {
      return this.shouldBeLiveMatches.filter(m => !(this.channelsByMatch[m.calMatchId] || []).length);
    },
    liveChannels() {
      return this.liveMatches.flatMap(m => this.channelsByMatch[m.calMatchId] || []);
    },
    distinctEncoders() {
      const seen = new Set();
      for (const ch of [...this.liveChannels, ...this.feedWindowChannels]) {
        const enc = ch.encoder?.trim();
        if (enc) seen.add(enc);
      }
      return [...seen].sort();
    },
    unreachableEncoders() {
      return this.distinctEncoders.filter(enc => this.encoderState[enc]?.unreachable);
    },
    streamingCount() {
      return this.liveChannels.filter(ch => this.isRunning(ch)).length;
    },
    summary() {
      const m = this.liveMatches.length;
      const c = this.liveChannels.length;
      const u = this.unconfiguredMatches.length;
      const base = `${m} live ${m === 1 ? 'match' : 'matches'} · ${c} ${c === 1 ? 'channel' : 'channels'} · ${this.streamingCount} streaming`;
      return u ? `${base} · ${u} not configured` : base;
    },
    configWarnings() {
      const out = [];
      for (const match of this.liveMatches) {
        for (const ch of this.channelsFor(match)) {
          const issues = this.configIssuesFor(ch);
          if (issues.length) {
            out.push({ calChannelId: ch.calChannelId, channelName: ch.channelName, match: `${match.team1} v ${match.team2}`, issues });
          }
        }
      }
      return out;
    },
    startTitle() {
      const n = this.startTargets.length;
      return n === 1 ? 'Start this channel?' : `Start ${n} channels?`;
    },
    stopTitle() {
      const n = this.stopTargets.length;
      return n === 1 ? 'Stop this channel?' : `Stop ${n} channels?`;
    },
    hasPendingChanges() {
      return Object.keys(this.localOrder).length > 0;
    }
  },
  watch: {
    liveChannels() {
      this.checkConfigs();
    },
    liveMatches(list) {
      // The match being reordered ended or vanished: drop the reorder state so other matches unlock
      if (this.reorderMatchId !== null && !this.savingOrder
          && !list.some(m => String(m.calMatchId) === String(this.reorderMatchId))) {
        this.reorderMatchId = null;
      }
    }
  },
  created() {
    this.loadMatches();
  },
  mounted() {
    this._countdownTimer = setInterval(() => {
      if (document.hidden || this.refreshPaused) return;
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.countdown = POLL_SECONDS;
        this.pollStats();
        this.loadChannelRecords();
        this.checkConfigs();
      }
    }, 1000);
    this._matchesTimer = setInterval(() => {
      if (!this.refreshPaused) this.loadMatches();
    }, MATCH_REFRESH_MS);
    this._onVisibilityChange = () => {
      if (!document.hidden) this.countdown = 0;
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this._onBeforeUnload = (e) => {
      if (this.reorderMatchId !== null && this.hasPendingChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._onBeforeUnload);
  },
  beforeUnmount() {
    clearInterval(this._countdownTimer);
    clearInterval(this._matchesTimer);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
  },
  methods: {
    fetchWindow() {
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      return { from: new Date(now - day).toISOString(), to: new Date(now + day).toISOString() };
    },
    // Channel records (order, status, active, live views) linked to calendar channels by channelName.
    // Reloaded with every stats poll so live viewers stay current. Fails soft: until they load, the rows show no channel info.
    // A call made while one is in flight waits for it and then loads again, so a save is never missed.
    loadChannelRecords() {
      if (this._channelsPromise) {
        return this._channelsPromise.then(() => this.loadChannelRecords());
      }
      this._channelsPromise = AdminService.getChannelsList(undefined, false).then((list) => {
        this.channelRecords = list || [];
        this.channelRecordsLoaded = true;
      }).catch((err) => {
        console.error('LiveEvents: error loading channels', err);
      }).finally(() => {
        this._channelsPromise = null;
      });
      return this._channelsPromise;
    },
    loadMatches() {
      const { from, to } = this.fetchWindow();
      const firstLoad = !this.calMatchesList.length;
      this.loading = firstLoad;
      return Promise.all([
        AdminService.getCalLeaguesList(true),
        AdminService.getAllCalMatches(from, to),
        AdminService.getAllCalChannels(from, to)
      ]).then(([leagues, matches, channels]) => {
        if (firstLoad) this.loadChannelRecords();
        this.calLeaguesList = leagues || [];
        this.calMatchesList = matches || [];
        this.calChannelsList = channels || [];
        this.loadError = '';
        const hasNewEncoder = this.distinctEncoders.some(enc => !this.encoderState[enc]);
        if (firstLoad || hasNewEncoder) this.pollStats();
      }).catch(err => {
        console.error('LiveEvents: error loading matches', err);
        this.loadError = err.response?.data?.message || err.message || 'Failed to load matches.';
      }).finally(() => {
        this.loading = false;
      });
    },
    pollStats() {
      if (this.pollInFlight) return;
      const encoders = this.distinctEncoders;
      if (!encoders.length) return;

      const next = { ...this.encoderState };
      for (const enc of encoders) {
        if (!next[enc]) next[enc] = { stats: null, lastOkAt: null, unreachable: false, pending: true };
      }
      this.encoderState = next;
      this.pollInFlight = true;

      Promise.allSettled(encoders.map(enc => XaccelService.getAllStreamStats(enc))).then(results => {
        const updated = { ...this.encoderState };
        results.forEach((result, i) => {
          const enc = encoders[i];
          if (result.status === 'fulfilled') {
            updated[enc] = { stats: statsMapFromList(result.value || []), lastOkAt: new Date(), unreachable: false, pending: false };
          } else {
            const prev = updated[enc] || {};
            updated[enc] = { stats: prev.stats || null, lastOkAt: prev.lastOkAt || null, unreachable: true, pending: false };
          }
        });
        this.encoderState = updated;
      }).finally(() => {
        this.pollInFlight = false;
      });
    },
    manualRefresh() {
      if (this.pollInFlight) return;
      this.countdown = POLL_SECONDS;
      this.pollStats();
      this.loadChannelRecords();
    },
    // After an action: refresh now, or on the next tick if a poll is already running.
    refreshAfterAction() {
      if (this.pollInFlight) {
        this.countdown = 0;
      } else {
        this.countdown = POLL_SECONDS;
        this.pollStats();
      }
      this.loadChannelRecords();
    },
    statFor(ch) {
      const entry = ch.encoder ? this.encoderState[ch.encoder.trim()] : null;
      if (!entry || !entry.stats) return null;
      return entry.stats.get(String(ch.encStreamId || '').trim()) || null;
    },
    isRunning(ch) {
      const stat = this.statFor(ch);
      return !!stat && isStreamRunning(stat.status);
    },
    channelsFor(match) {
      return this.channelsByMatch[match.calMatchId] || [];
    },
    // Running channels whose encoder is reachable.
    stoppableFor(match) {
      return this.channelsFor(match).filter(ch => this.isRunning(ch) && !this.encoderState[ch.encoder.trim()]?.unreachable);
    },
    configIssuesFor(ch) {
      const entry = this.configMeta[ch.calChannelId];
      return entry ? checkChannelConfig(ch, entry.meta) : [];
    },
    // Reads each live linked channel's encoder stream once (again after a few minutes) and keeps it for the setup check.
    async checkConfigs() {
      const now = Date.now();
      const due = this.liveChannels.filter(ch => {
        if (!isLinkedChannel(ch)) return false;
        if (this.encoderState[ch.encoder.trim()]?.unreachable) return false;
        const entry = this.configMeta[ch.calChannelId];
        return !entry || now - entry.at > CONFIG_CHECK_MS;
      });
      this._configBusy = this._configBusy || new Set();
      await Promise.all(due.map(async ch => {
        if (this._configBusy.has(ch.calChannelId)) return;
        this._configBusy.add(ch.calChannelId);
        try {
          const meta = await XaccelService.getXaccelStreamMetadata(ch.encoder.trim(), encStreamIdentifier(ch.encStreamId));
          this.configMeta = { ...this.configMeta, [ch.calChannelId]: { meta: meta || {}, at: Date.now() } };
        } catch (err) {
          console.error('LiveEvents: config check failed', ch, err);
        } finally {
          this._configBusy.delete(ch.calChannelId);
        }
      }));
    },
    unconfiguredReason(match) {
      const total = this.calChannelsList.filter(ch => String(ch.calMatchId) === String(match.calMatchId)).length;
      if (!total) return 'No channels are configured for this match.';
      return `${total} ${total === 1 ? 'channel exists' : 'channels exist'} but none ${total === 1 ? 'is' : 'are'} active.`;
    },
    inWindow(match) {
      const now = new Date();
      return !!match.startTime && new Date(match.startTime) <= now && now < new Date(this.effectiveEnd(match));
    },
    // Linked, stopped channels of a match that is inside its time window, on a reachable encoder.
    startableFor(match) {
      if (!this.inWindow(match)) return [];
      return this.channelsFor(match).filter(ch => {
        const stat = this.statFor(ch);
        return !!stat && !isStreamRunning(stat.status) && !this.encoderState[ch.encoder.trim()]?.unreachable;
      });
    },
    shouldBeLiveIdle(match) {
      return this.inWindow(match) && !this.channelsFor(match).some(ch => this.isRunning(ch));
    },
    askStart(channels) {
      if (this.reorderActive) return;
      this.startTargets = channels.filter(ch => !this.actionState[ch.calChannelId]?.busy);
    },
    async confirmStart() {
      const targets = this.startTargets;
      this.startTargets = [];
      if (!targets.length) return;
      targets.forEach(ch => this.setAction(ch.calChannelId, { busy: true, error: '' }));
      for (const ch of targets) {
        try {
          await XaccelService.startXaccelStream(ch.encoder.trim(), encStreamIdentifier(ch.encStreamId));
        } catch (err) {
          console.error('LiveEvents: start failed', ch, err);
          this.setAction(ch.calChannelId, { error: `Start failed on ${ch.encoder}.` });
        }
      }
      targets.forEach(ch => this.setAction(ch.calChannelId, { busy: false }));
      this.refreshAfterAction();
    },
    matchBusy(match) {
      return this.channelsFor(match).some(ch => this.actionState[ch.calChannelId]?.busy);
    },
    setAction(calChannelId, patch) {
      const prev = this.actionState[calChannelId] || { busy: false, error: '' };
      this.actionState = { ...this.actionState, [calChannelId]: { ...prev, ...patch } };
    },

    // Stops on the encoder through the b-api first, then records the stop on cal_channel (status=false).
    askStop(channels) {
      if (this.reorderActive) return;
      this.stopTargets = channels.filter(ch => !this.actionState[ch.calChannelId]?.busy);
    },
    async confirmStop() {
      const targets = this.stopTargets;
      this.stopTargets = [];
      if (!targets.length) return;

      targets.forEach(ch => this.setAction(ch.calChannelId, { busy: true, error: '' }));
      const stopped = [];
      for (const ch of targets) {
        try {
          await XaccelService.stopXaccelStream(ch.encoder.trim(), encStreamIdentifier(ch.encStreamId));
          stopped.push(ch);
        } catch (err) {
          console.error('LiveEvents: stop failed', ch, err);
          this.setAction(ch.calChannelId, { error: `Stop failed on ${ch.encoder}.` });
        }
      }

      if (stopped.length) {
        try {
          await AdminService.stopCalChannels(stopped.map(ch => ch.calChannelId));
        } catch (err) {
          console.error('LiveEvents: recording the stop failed', err);
          const msg = err.response?.data?.message || err.message || '';
          stopped.forEach(ch => this.setAction(ch.calChannelId, { error: `Stopped on encoder, not recorded. ${msg}`.trim() }));
        }
      }

      targets.forEach(ch => this.setAction(ch.calChannelId, { busy: false }));
      this.refreshAfterAction();
      this.loadMatches();
    },

    async toggleActive(ch, record) {
      if (!record || this.actionState[ch.calChannelId]?.busy) return;
      this.setAction(ch.calChannelId, { busy: true, error: '' });
      try {
        await AdminService.updateChannel({ ...record, status: null, active: !record.active });
        await this.loadChannelRecords();
      } catch (err) {
        console.error('LiveEvents: saving active state failed', err);
        this.setAction(ch.calChannelId, { error: 'Could not change the active state.' });
      } finally {
        this.setAction(ch.calChannelId, { busy: false });
      }
    },

    // Saves the dragged order of one match. Returns false if any update failed.
    async saveOrderFor(matchId) {
      const match = this.liveMatches.find(m => String(m.calMatchId) === String(matchId));
      if (!match) return true;
      const updates = [];
      this.channelsFor(match).forEach((ch, idx) => {
        const record = this.channelByName[ch.channelName];
        if (record && Number(record.orderNumber) !== idx + 1) {
          updates.push({ ...record, orderNumber: idx + 1, active: null, status: null });
        }
      });
      try {
        for (const record of updates) {
          await AdminService.updateChannel(record);
        }
        return true;
      } catch (err) {
        console.error('LiveEvents: saving order failed', err);
        this.orderErrors = { ...this.orderErrors, [matchId]: 'Could not save the new order.' };
        return false;
      }
    },

    isReordering(match) {
      return this.reorderMatchId !== null && String(this.reorderMatchId) === String(match.calMatchId);
    },
    startReorder(match) {
      this.orderErrors = { ...this.orderErrors, [match.calMatchId]: '' };
      this.reorderMatchId = match.calMatchId;
    },
    async finishReorder(match) {
      const matchId = match.calMatchId;
      if (!this.localOrder[matchId]) {
        this.reorderMatchId = null;
        return;
      }
      this.savingOrder = true;
      let ok = false;
      try {
        ok = await this.saveOrderFor(matchId);
      } finally {
        await this.loadChannelRecords();
        this.savingOrder = false;
        if (ok) {
          const rest = { ...this.localOrder };
          delete rest[matchId];
          this.localOrder = rest;
          this.reorderMatchId = null;
        }
      }
    },

    onReorder(match, newList) {
      const matchId = match.calMatchId;
      this.orderErrors = { ...this.orderErrors, [matchId]: '' };
      const hasChange = newList.some((ch, idx) => {
        const record = this.channelByName[ch.channelName];
        return record && Number(record.orderNumber) !== idx + 1;
      });
      if (!hasChange) {
        const rest = { ...this.localOrder };
        delete rest[matchId];
        this.localOrder = rest;
        return;
      }
      this.localOrder = { ...this.localOrder, [matchId]: newList.map(ch => ch.calChannelId) };
    },

    effectiveEnd(match) {
      return match.endTime || match.startTime;
    },
    leagueNameFor(calLeagueId) {
      const lg = this.leaguesById[calLeagueId];
      return lg ? lg.name : `League #${calLeagueId}`;
    },
    formatClock(date) {
      return date.toLocaleTimeString(undefined, { hour12: false });
    },
    formatLocalTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    formatRange(match) {
      const start = this.formatLocalTime(match.startTime);
      return match.endTime ? `${start} – ${this.formatLocalTime(match.endTime)}` : start;
    },
    encoderTone(enc) {
      const entry = this.encoderState[enc];
      if (!entry || entry.pending) return 'loading';
      return entry.unreachable ? 'down' : 'ok';
    },
    encoderLabel(enc) {
      const entry = this.encoderState[enc];
      if (!entry || entry.pending) return 'loading…';
      if (entry.unreachable) return entry.lastOkAt ? `unreachable · last ok ${this.formatClock(entry.lastOkAt)}` : 'unreachable';
      return `ok · updated ${this.formatClock(entry.lastOkAt)}`;
    },
    staleNote(enc) {
      const at = this.encoderState[enc]?.lastOkAt;
      return at
        ? `Showing last known values for its channels (updated ${this.formatClock(at)}).`
        : 'No values received yet.';
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
